import React from 'react';
import { ModoTiendas } from '../../utils/modoTiendas';
import MultiCombo from '../MultiCombo';
import PanelFiltros from '../PanelFiltros';
import type { Store } from '../../data/stores';
import { colors } from '../../styles/tokens';
import {
  applyCadena,
  applyRegional,
  applySupervisor,
  applyTienda,
  claveFiltros,
  computeFilterOptions,
  GlobalFiltros,
} from '../../utils/filtros';
import { ESTATUS_OPTIONS } from '../../utils/negados';
import {
  BorradorPeriodo,
  borradorDePeriodo,
  clavePeriodo,
  Periodo,
  periodoDeBorrador,
  validarBorrador,
} from '../../utils/periodo';
import { useEsMovil } from '../../hooks/useEsMovil';
import { useBorrador } from '../../hooks/useBorrador';

/** Lo que aplica el panel de Global: la cascada + estatus y el periodo. */
export interface FiltrosAplicados {
  filtros: GlobalFiltros;
  periodo: Periodo;
}

export interface FiltrosProps {
  /** Catálogo completo (SIN filtrar) usado para calcular los pools de cada combo. */
  stores: Store[];
  /**
   * Filtros APLICADOS (los que ven las consultas). El panel los copia a un
   * borrador y sólo los devuelve, completos, por `onAplicar`.
   *
   * 2026-08-03 (punto 3): los 4 de la cascada son MULTISELECT (vacío = sin
   * filtro). `estatus` es el Estatus de PRODUCTO (PT5), filtro SERVER-SIDE.
   */
  filtros: GlobalFiltros;
  /** Periodo APLICADO, compartido con la vista Tienda. */
  periodo: Periodo;
  /**
   * «Aplicar» (o Enter en una fecha, o un atajo): aplica TODO el panel de una
   * vez — cascada, estatus y fechas. Sólo se llama con un borrador válido.
   */
  onAplicar: (siguiente: FiltrosAplicados) => void;
  /**
   * Switch del Header — cuando NO es «todas» (y viene `activeStoreKeys`), el
   * dropdown Tienda se acota a las tiendas activas / sin uso del periodo
   * APLICADO (pendiente #5, ej. "Regina").
   */
  modo?: ModoTiendas;
  /** `(chainId, branchOfficeId)` (formato `rowKey`) de las tiendas activas en el periodo. */
  activeStoreKeys?: Set<string>;
  /**
   * `false` oculta el campo Estatus de PRODUCTO. Lo usa la vista de Presupuesto:
   * el estatus filtra el embudo de consultas, no la venta contra la meta.
   */
  mostrarEstatus?: boolean;
  /**
   * Presupuesto: añade «Mes en curso» y deja elegir fechas futuras (la meta del
   * mes llega hasta fin de mes). La meta es un instrumento MENSUAL.
   */
  rangosDeMeta?: boolean;
}

interface Borrador {
  filtros: GlobalFiltros;
  periodo: BorradorPeriodo;
}

const claveAplicada = (f: GlobalFiltros, p: Periodo) => `${claveFiltros(f)}#${clavePeriodo(p)}`;

/**
 * Panel de filtros de la vista Global (y de Presupuesto): zona ÁMBITO con 5
 * campos —Regional · Supervisor · Cadena · Tienda · Estatus, los 4 primeros en
 * cascada— y la zona PERIODO común (`PanelFiltros`).
 *
 * TODO es borrador hasta «Aplicar» (decisión del usuario, 12-oct): elegir un
 * Regional ya no recalcula el tablero ni dispara consultas; el chip de alcance y
 * las cifras siguen mostrando lo aplicado. La cascada (resetear aguas abajo) se
 * hace sobre el borrador con los mismos `apply*` de `utils/filtros`.
 *
 * EN TELÉFONO (`useEsMovil`) la rejilla de 5 campos pasa a 1 por fila y los
 * combos abandonan la variante `flat` por la de caja, que mide lo que un dedo.
 */
const Filtros: React.FC<FiltrosProps> = ({
  stores,
  filtros,
  periodo,
  onAplicar,
  modo,
  activeStoreKeys,
  mostrarEstatus = true,
  rangosDeMeta = false,
}) => {
  const esMovil = useEsMovil();

  const { borrador, setBorrador, sucio } = useBorrador<Borrador>(
    () => ({ filtros, periodo: borradorDePeriodo(periodo) }),
    claveAplicada(filtros, periodo),
    (b) => claveAplicada(b.filtros, periodoDeBorrador(b.periodo)),
  );

  const error = validarBorrador(borrador.periodo, { permitirFuturo: rangosDeMeta });

  const { regionalOpts, supervisorOpts, cadenaOpts, tiendaOpts } = computeFilterOptions(
    stores,
    borrador.filtros,
    { modo, activeStoreKeys },
  );

  const cambiarFiltros = (fn: (f: GlobalFiltros) => GlobalFiltros) =>
    setBorrador((b) => ({ ...b, filtros: fn(b.filtros) }));

  const aplicar = () => {
    if (error) return;
    onAplicar({ filtros: borrador.filtros, periodo: periodoDeBorrador(borrador.periodo) });
  };

  // Un atajo aplica al instante el panel COMPLETO con ese periodo: si había un
  // Regional elegido sin aplicar, entra también. Lo que se ve en el panel es lo
  // que queda aplicado.
  // ⚠️ Y deja el BORRADOR en ese periodo. Si el atajo es el que ya está aplicado
  // (semana anterior con fechas tecleadas encima), la pantalla no cambia nada —
  // devuelve `prev`— y `useBorrador`, que reinicia por cambio de llave, no se
  // enteraría: el panel seguiría en la fecha tecleada, con «Cambios sin aplicar»
  // y la pill apagada, y un «Aplicar» posterior aplicaría lo tecleado.
  const atajo = (p: Periodo) => {
    setBorrador((b) => ({ ...b, periodo: borradorDePeriodo(p) }));
    onAplicar({ filtros: borrador.filtros, periodo: p });
  };

  // Cada celda de la grid de ÁMBITO: separador `border-left` fino salvo la
  // primera. En móvil los campos van uno debajo de otro y aprovechan el ancho.
  const cellStyle = (index: number, last: boolean): React.CSSProperties =>
    esMovil
      ? { padding: 0, minWidth: 0 }
      : {
          borderLeft: index === 0 ? undefined : `1px solid ${colors.rowBorder}`,
          padding: index === 0 ? '0 14px 0 0' : last ? '0 6px 0 14px' : '0 14px',
          minWidth: 0,
        };

  const f = borrador.filtros;

  const ambito = (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: esMovil ? '1fr' : `repeat(${mostrarEstatus ? 5 : 4}, minmax(0, 1fr))`,
        gap: esMovil ? 12 : undefined,
      }}
    >
      <div style={cellStyle(0, false)}>
        <MultiCombo
          label="Regional"
          testid="regional"
          allLabel="Todos los regionales"
          value={f.regional}
          options={regionalOpts}
          onChange={(v) => cambiarFiltros((x) => applyRegional(x, v))}
          accent
          /* `flat` en escritorio para no verse como card-sobre-card; en
             móvil la variante de caja da ~48 px tocables. */
          flat={!esMovil}
          placeholder="Buscar regional…"
        />
      </div>
      <div style={cellStyle(1, false)}>
        <MultiCombo
          label="Supervisor"
          testid="supervisor"
          allLabel="Todos los supervisores"
          value={f.supervisor}
          options={supervisorOpts}
          onChange={(v) => cambiarFiltros((x) => applySupervisor(x, v))}
          accent
          flat={!esMovil}
          placeholder="Buscar supervisor…"
        />
      </div>
      <div style={cellStyle(2, false)}>
        <MultiCombo
          label="Cadena"
          testid="cadena"
          allLabel="Todas las cadenas"
          value={f.cadena}
          options={cadenaOpts}
          onChange={(v) => cambiarFiltros((x) => applyCadena(x, v))}
          flat={!esMovil}
          placeholder="Buscar cadena…"
        />
      </div>
      <div style={cellStyle(3, !mostrarEstatus)}>
        <MultiCombo
          label="Tienda"
          testid="tienda"
          allLabel="Todas las tiendas"
          value={f.tienda}
          options={tiendaOpts}
          onChange={(v) => cambiarFiltros((x) => applyTienda(x, v))}
          flat={!esMovil}
          placeholder="Buscar tienda…"
        />
      </div>
      {mostrarEstatus && (
        <div style={cellStyle(4, true)}>
          <MultiCombo
            label="Estatus"
            value={f.estatus}
            options={ESTATUS_OPTIONS}
            onChange={(v) => cambiarFiltros((x) => ({ ...x, estatus: v }))}
            flat={!esMovil}
            placeholder="Estatus de producto…"
          />
        </div>
      )}
    </div>
  );

  return (
    <PanelFiltros
      testid="filtros-global"
      tituloAmbito="Ámbito"
      ambito={ambito}
      /* ⚠️ La base es 360, no 660: con `flex-wrap` el navegador decide si las
         dos zonas caben usando la BASE, y con 660 PERIODO caía a una segunda
         fila por debajo de 1440 px. `flex-grow: 1` la estira igual en
         pantallas anchas. */
      baseAmbito="1 1 360px"
      borradorPeriodo={borrador.periodo}
      onBorradorPeriodo={(p) => setBorrador((b) => ({ ...b, periodo: p }))}
      onAtajo={atajo}
      sucio={sucio}
      error={error}
      onAplicar={aplicar}
      rangosDeMeta={rangosDeMeta}
    />
  );
};

export default Filtros;
